import React from "react";
import { Link } from "react-router-dom";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { Section, SectionLabel, SectionTitle } from "@/components/layout/Section";
import { Reveal } from "@/lib/motion";
import { BrpLogo } from "@/components/brand/BrpLogo";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

const principles = [
  { n: "01", title: "People before platforms." },
  { n: "02", title: "Simplicity before complexity." },
  { n: "03", title: "Purpose before features." },
  { n: "04", title: "Long-term thinking over short-term trends." },
  { n: "05", title: "Technology should make progress feel possible." },
];

export default function AboutPage() {
  return (
    <SiteLayout>
      {/* HERO */}
      <section className="relative overflow-hidden min-h-[560px] sm:min-h-[640px] flex items-center">
        <div aria-hidden className="absolute inset-0">
          <picture>
            <source srcSet="/hero-about.webp" type="image/webp" />
            <img
              src="/hero-about.jpg"
              alt=""
              className="h-full w-full object-cover object-center"
              loading="eager"
              fetchpriority="high"
            />
          </picture>
          {/* Editorial overlay: dark center vignette to keep centered content legible */}
          <div className="absolute inset-0 bg-[radial-gradient(60%_60%_at_50%_50%,rgba(11,13,18,0.55)_0%,rgba(11,13,18,0.85)_60%,rgba(11,13,18,0.95)_100%)]" />
          <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--background))]/40 via-transparent to-[hsl(var(--background))]" />
          <div className="absolute inset-0 brp-hero-glow-orange opacity-40" />
        </div>
        <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-10 py-24 sm:py-32 lg:py-40 flex flex-col items-center text-center gap-8">
          <Reveal>
            <BrpLogo size={72} tone="dark" />
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-display font-semibold tracking-[-0.03em] text-3xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
              Solving Today. Inventing Tomorrow.
            </h1>
          </Reveal>
        </div>
      </section>

      {/* SECTION 1 - Technology with people at the center */}
      <Section tone="dark" py="lg" className="border-t border-border/70">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-6">
            <SectionLabel tone="orange">About BRP Tech</SectionLabel>
            <SectionTitle className="mt-4" size="xl">Technology with people at the center.</SectionTitle>
          </Reveal>
          <Reveal className="lg:col-span-6 space-y-4 text-[hsl(var(--brp-ink-dim))] leading-relaxed" delay={0.08}>
            <p>BRP Tech is a human-centered technology company. We design thoughtful software, intelligent systems, and digital experiences that remove operational friction, solve meaningful problems, and create lasting impact.</p>
            <p className="text-[hsl(var(--brp-ink))]">Technology should empower people — not overwhelm them.</p>
          </Reveal>
        </div>
      </Section>

      {/* SECTION 2 - What We Believe */}
      <Section tone="paper" py="lg">
        <Reveal>
          <SectionLabel className="!text-[hsl(var(--brp-orange))]">What We Believe</SectionLabel>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-8">
          {principles.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.05}>
              <div data-testid="principle-card" className="pt-6 border-t border-[hsl(var(--brp-border-on-paper))] h-full">
                <div data-testid="principle-number" className="font-mono text-xs tracking-widest text-[hsl(var(--brp-orange))]">{p.n}</div>
                <h3 data-testid="principle-title" className="mt-4 text-lg font-display font-semibold tracking-tight leading-snug">
                  {p.title}
                </h3>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* SECTION 3 - Two Ways We Build */}
      <Section tone="dark" py="lg">
        <Reveal>
          <SectionLabel tone="orange">Two Ways We Build</SectionLabel>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          <Reveal>
            <div className="h-full rounded-2xl border border-border/70 bg-[hsl(var(--brp-surface-2))] p-8">
              <div className="h-12 w-12 rounded-xl border border-border/70 bg-background/40 grid place-items-center text-[hsl(var(--brp-orange))] mb-6">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2"/><circle cx="10" cy="7" r="3"/><path d="M21 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </div>
              <h3 className="text-2xl font-display font-semibold tracking-tight">For Organizations</h3>
              <p className="mt-4 text-[hsl(var(--brp-ink-dim))] leading-relaxed">
                We partner with organizations to design custom software, intelligent systems, workflow automation, and strategy-backed technology solutions that strengthen operations, empower teams, and create sustainable growth.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="h-full rounded-2xl border border-border/70 bg-[hsl(var(--brp-surface-2))] p-8">
              <div className="h-12 w-12 rounded-xl border border-border/70 bg-background/40 grid place-items-center text-[hsl(var(--brp-orange))] mb-6">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M8 8h8v8H8z"/><circle cx="12" cy="12" r="1"/></svg>
              </div>
              <h3 className="text-2xl font-display font-semibold tracking-tight">From the Innovation Vault</h3>
              <p className="mt-4 text-[hsl(var(--brp-ink-dim))] leading-relaxed">
                We research industries, identify overlooked opportunities, and develop proprietary software, mobile applications, and emerging technologies that unlock entirely new possibilities across business, media, education, entertainment, lifestyle, and beyond.
              </p>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* SECTION 4 - Where We Work */}
      <Section tone="paper" py="lg">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-6">
            <SectionLabel className="!text-[hsl(var(--brp-orange))]">Where We Work</SectionLabel>
            <SectionTitle className="mt-4" size="xl">Headquartered in the Metro Nashville area of Tennessee.</SectionTitle>
          </Reveal>
          <Reveal className="lg:col-span-6 space-y-4 text-[hsl(var(--brp-ink-muted-on-paper))] leading-relaxed" delay={0.08}>
            <p>We partner with organizations wherever technology can create meaningful impact.</p>
            <p>Our reach extends beyond geography. Our standards remain personal, our partnerships intentional, and our commitment unwavering.</p>
          </Reveal>
        </div>
      </Section>

      {/* SECTION 5 - Founded on a simple belief */}
      <Section tone="dark" py="lg">
        <Reveal>
          <SectionLabel tone="orange">Founded on a Simple Belief</SectionLabel>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <Reveal className="lg:col-span-3">
            <div data-testid="founder-signature-block" className="relative rounded-2xl border border-border/70 bg-[hsl(var(--brp-surface-2))] p-4 w-32 h-32 grid place-items-center overflow-hidden">
              <div aria-hidden className="absolute inset-0" style={{ background: "radial-gradient(circle at 30% 30%, rgba(249,115,22,0.28), rgba(0,0,0,0) 60%)" }} />
              <span className="relative font-display text-6xl font-bold text-[hsl(var(--brp-orange))] leading-none">G</span>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-9 space-y-4 text-[hsl(var(--brp-ink-dim))] leading-relaxed" delay={0.08}>
            <p className="text-lg text-[hsl(var(--brp-ink))]">
              BRP Tech was founded by a regular person who believed the future of technology should feel approachable, practical, and deeply human.
            </p>
            <p>
              That belief continues to shape everything we create — transforming ambitious ideas into thoughtful software, intuitive experiences, and intelligent systems that help organizations move forward with confidence.
            </p>
          </Reveal>
        </div>
      </Section>

      {/* CLOSING */}
      <Section tone="paper" py="lg">
        <div className="flex flex-col items-center text-center gap-8">
          <Reveal>
            <SectionTitle size="xl" className="max-w-3xl">Technology should make progress feel possible.</SectionTitle>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="text-[hsl(var(--brp-ink-muted-on-paper))] max-w-xl leading-relaxed">That’s what we build every day at BRP Tech.</p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/contact">
              <Button size="lg" className="rounded-full bg-[hsl(var(--brp-ink-on-paper))] hover:bg-black text-white px-8 h-12">
                Let’s Talk <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </Reveal>
        </div>
      </Section>
    </SiteLayout>
  );
}
